import { IonBadge } from '@ionic/react';
import { NavContext } from '@ionic/react';
import React, { useCallback, useContext, useMemo } from 'react';

import { Avatar, Menu } from '@/components/common';
import { Notifications } from '@/components/dashboard';
import { Icon } from '@/components/memo';
import {
  useAuth,
  useClinic,
  useDentist,
  useNotifications,
  useTabs
} from '@/contexts';
import { getInitials } from '@/utils/helpers/getInitials';
import { ROUTES } from '@/utils/routes';
import { MenuItem } from '@/utils/types';
import {
  logOutOutline,
  notificationsOutline,
  swapHorizontalOutline
} from 'ionicons/icons';

import './HeaderIcons.css';

interface HeaderIconsProps {
  tab: string;
}

const HeaderIcons: React.FC<HeaderIconsProps> = ({ tab }) => {
  const { signOut } = useAuth();
  const { selectDentist, selectedDentist: dentist } = useDentist();
  const { isClinic } = useClinic();
  const { navigate } = useContext(NavContext);
  const {
    unreadCount,
    readNotification,
    isNotificationsOpen,
    setIsNotificationsOpen
  } = useNotifications();
  const { selectedTab } = useTabs();

  const active = useMemo(() => tab === selectedTab, [tab, selectedTab]);

  const onSwitchDentist = useCallback(async () => {
    await selectDentist(null);
    navigate(ROUTES.SELECT_DENTIST);
  }, [selectDentist, navigate]);

  const onSignOut = useCallback(async () => {
    await selectDentist(null);
    signOut();
  }, [selectDentist, signOut]);

  const onOpenNotifications = useCallback(() => {
    setIsNotificationsOpen(true);
  }, []);

  const onCloseNotifications = useCallback(() => {
    setIsNotificationsOpen(false);
    readNotification();
  }, [readNotification]);

  const menuItems: MenuItem[] = useMemo(
    () => [
      {
        icon: swapHorizontalOutline,
        label: `Switch ${isClinic ? 'Dentist' : 'Clinic'}`,
        onClick: onSwitchDentist
      },
      {
        icon: logOutOutline,
        label: 'Sign Out',
        onClick: onSignOut
      }
    ],
    [isClinic, onSwitchDentist, onSignOut]
  );

  if (!active) return null;

  return (
    <>
      <div className="header-icons">
        <div
          className="header-icons__notification-icon"
          onClick={onOpenNotifications}
        >
          <Icon icon={notificationsOutline} />
          {unreadCount > 0 && <IonBadge color="danger">{unreadCount}</IonBadge>}
        </div>
        <Menu
          triggerClassName="header-icons__avatar-icon"
          triggerContent={<Avatar initials={getInitials(dentist)} />}
          items={menuItems}
          className="avatar-popover"
        />
      </div>

      <Notifications
        isOpen={isNotificationsOpen}
        onClose={onCloseNotifications}
      />
    </>
  );
};

export default HeaderIcons;
